// اجزای مشترک صفحات مطالبه بیمه (SCR-STAFF-853 تا 856؛ AP-INS-02): کلیدهای Query، فهرست‌های شمارشی، رنگ وضعیت و نشان نقص.
// نقص کنترل کیفیت همیشه با متن و راهنمای i18n نمایش داده می‌شود، چون «چرا این قلم ارسال نمی‌شود؟» پرسش روزمره کارشناس بیمه
// است (AC-INS-062.2).
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import type {
  InsAppealStatus,
  InsClaimBatchStatus,
  InsClaimCheckCode,
  InsClaimDefectOwner,
  InsClaimDefectView,
  InsClaimItemStatus,
  InsDeductionReasonCode,
} from '@phoenix/api-client';
import { useTranslation } from '@phoenix/i18n';
import { StatusChip, faDigits, type StatusTone } from '@phoenix/ui';

export const CLAIMS_PATH = '/insurance/claims';
export const DEDUCTIONS_PATH = '/insurance/deductions';
export const REMITTANCES_PATH = '/insurance/remittances';
export const CLAIM_REPORTS_PATH = '/insurance/claim-reports';

export type ClaimStatus = NonNullable<InsClaimItemStatus>;
export type DeductionReason = NonNullable<InsDeductionReasonCode>;

export const CLAIM_STATUSES: readonly ClaimStatus[] = [
  'Pending',
  'NeedsFix',
  'Ready',
  'Batched',
  'Submitted',
  'Returned',
  'Settled',
  'Void',
];

export const DEFECT_OWNERS: readonly NonNullable<InsClaimDefectOwner>[] = ['Reception', 'Physician', 'InsuranceClerk'];

export const DEDUCTION_REASONS: readonly DeductionReason[] = [
  'FeeScheduleDifference',
  'MissingDocument',
  'MissingSignature',
  'InvalidDiagnosis',
  'NotCovered',
  'CapExceeded',
  'NoPreAuth',
  'LateSubmission',
  'Duplicate',
  'EligibilityIssue',
  'Other',
];

/** کلیدهای Query مطالبات (به ازای مستأجر تا تغییر مستأجر Cache را جابه‌جا نکند). */
export const claimKeys = {
  items: (tenantId: string | undefined, filter: string) => ['ins', 'claim-items', tenantId, filter] as const,
  item: (tenantId: string | undefined, claimItemId: string) => ['ins', 'claim-item', tenantId, claimItemId] as const,
  batches: (tenantId: string | undefined, insurerId?: string) => ['ins', 'claim-batches', tenantId, insurerId ?? null] as const,
  defects: (tenantId: string | undefined, owner: string) => ['ins', 'claim-defects', tenantId, owner] as const,
  policy: (tenantId: string | undefined) => ['ins', 'deduction-policy', tenantId] as const,
  appeals: (tenantId: string | undefined, insurerId?: string) => ['ins', 'appeals', tenantId, insurerId ?? null] as const,
  remittances: (tenantId: string | undefined, insurerId?: string) => ['ins', 'remittances', tenantId, insurerId ?? null] as const,
  profiles: (tenantId: string | undefined, insurerId?: string) => ['ins', 'settlement-profiles', tenantId, insurerId ?? null] as const,
  aging: (tenantId: string | undefined, filter: string) => ['ins', 'claim-aging', tenantId, filter] as const,
  analytics: (tenantId: string | undefined, filter: string) => ['ins', 'claim-deductions', tenantId, filter] as const,
  performance: (tenantId: string | undefined, filter: string) => ['ins', 'insurer-performance', tenantId, filter] as const,
};

export function claimTone(status: InsClaimItemStatus | undefined): StatusTone {
  switch (status) {
    case 'Ready':
    case 'Settled':
      return 'active';
    case 'NeedsFix':
    case 'Returned':
      return 'error';
    case 'Void':
      return 'inactive';
    default:
      return 'pending';
  }
}

export function batchTone(status: InsClaimBatchStatus | undefined): StatusTone {
  switch (status) {
    case 'Submitted':
    case 'Reconciled':
      return 'active';
    case 'Closed':
      return 'inactive';
    default:
      return 'pending';
  }
}

export function appealTone(status: InsAppealStatus | undefined): StatusTone {
  switch (status) {
    case 'Accepted':
      return 'active';
    case 'Rejected':
      return 'error';
    case 'PartiallyAccepted':
      return 'pending';
    default:
      return 'pending';
  }
}

/** نرخ در ده‌هزار را درصد با ارقام فارسی می‌کند (۱۲۵۰ ← ۱۲٫۵). */
export function ratePercent(basisPoints: number | string | undefined): string {
  return faDigits(Number(basisPoints ?? 0) / 100);
}

/**
 * نشان نواقص باز یک قلم (AC-INS-062.2): هر نقص یک نشان با مسئول رفع و راهنمای کامل در Tooltip، تا کارشناس بیمه بداند چه کسی
 * باید چه کاری کند و قلم چرا ارسال نمی‌شود.
 */
export function ClaimDefects({ defects }: { defects: readonly InsClaimDefectView[] | undefined }) {
  const { t } = useTranslation('ins');
  const open = (defects ?? []).filter((defect) => defect.resolvedAt === null);

  if (open.length === 0) {
    return null;
  }

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap data-testid="ins-claim-defects">
      {open.map((defect) => (
        <Tooltip key={defect.check} title={t(`ins:claim_check_hint.${defect.check}`)}>
          <span data-testid={`ins-claim-defect-${defect.check}`}>
            <StatusChip tone="error" labelKey={`ins:claim_check.${defect.check}`} />
          </span>
        </Tooltip>
      ))}
    </Stack>
  );
}

/** نشان مسئول رفع یک بررسی. */
export function DefectOwner({ check, owner }: { check: InsClaimCheckCode; owner: InsClaimDefectOwner }) {
  const { t } = useTranslation('ins');
  return (
    <Tooltip title={t(`ins:claim_check_hint.${check}`)}>
      <span data-testid={`ins-defect-owner-${check}`}>
        <StatusChip tone="pending" labelKey={`ins:defect_owner.${owner}`} />
      </span>
    </Tooltip>
  );
}
